/* eslint-disable new-cap */
import {html, LitElement} from 'lit';
import noUiSlider from 'nouislider';
import {createRef, ref} from 'lit/directives/ref.js';
import styles from './facet-rangeslider.scss?as=stylesheet';

/**
 * @see https://stackoverflow.com/a/43606883
 */
export const roundToIncrement = (value, increment, fn = 'floor') => {
	const interval = 1 / increment;

	return Math[fn](value * interval) / interval;
};

const formatter = new Intl.NumberFormat('en-US', {
	currency: 'USD',
	style: 'currency'
});

export const formatPrice = (str) => {
	return formatter.format(str);
};

export class FacetRangeslider extends LitElement {
	static get styles () {
		return [styles];
	}

	static get properties () {
		return {
			code: {
				type: String
			},
			high: {
				type: Number
			},
			low: {
				type: Number
			},
			prompt: {
				type: String
			},
			selectedHigh: {
				attribute: 'selected-high',
				type: Number
			},
			selectedLow: {
				attribute: 'selected-low',
				type: Number
			}
		};
	}

	static formAssociated = true;

	slider = createRef();

	lowPrompt = createRef();

	highPrompt = createRef();

	constructor () {
		super();
		this._internals = this.attachInternals();
	}

	get margin () {
		return Math.round((this.high - this.low) * 0.05);
	}

	get sliderFormat () {
		return {
			from: value => {
				return Math.round(value);
			},
			to: value => {
				return formatPrice(value);
			}
		};
	}

	get sliderOptions () {
		return {
			ariaFormat: this.sliderFormat,
			connect: true,
			format: this.sliderFormat,
			handleAttributes: [
				{
					'aria-label': `Minimum ${this.prompt}`
				},
				{
					'aria-label': `Max ${this.prompt}`
				}
			],
			margin: this.margin,
			range: {
				max: [this.high],
				min: [this.low]
			},
			start: [
				this.selectedLow,
				this.selectedHigh
			],
			step: 1
		};
	}

	firstUpdated () {
		noUiSlider.create(
			this.slider.value,
			this.sliderOptions
		);

		// Bind change event listener
		this.slider.value.noUiSlider.on('change', this.handleChange.bind(this));

		if (this.lowPrompt?.value && this.highPrompt?.value) {
			this.slider.value.noUiSlider.on('update', this.handleUpdate.bind(this));
		}

		if (this.low !== this.selectedLow || this.high !== this.selectedHigh) {
			this._internals.setFormValue(`${this.selectedLow}-${this.selectedHigh}`);
		}
	}

	handleUpdate (formattedValues) {
		this.lowPrompt.value.innerText = formattedValues[0];
		this.highPrompt.value.innerText = formattedValues[1];
	}

	handleChange (formattedValues, handleNumber, [
		low,
		high
	]) {
		if (low === this.selectedLow && high === this.selectedHigh) {
			this._internals.setFormValue('');
			return;
		}

		const formattedLow = roundToIncrement(low, 0.1);
		const formattedhigh = roundToIncrement(high, 0.1);

		if (formattedLow === this.low && formattedhigh === this.high) {
			this._internals.setFormValue('');
		} else {
			this._internals.setFormValue(`${formattedLow}-${formattedhigh}`);
		}

		this._internals?.form?.submit();
	}

	updated () {
		this.slider.value.noUiSlider.updateOptions(this.sliderOptions);
	}

	render () {
		return html`
			<div class="value-prompts">
				<div ${ref(this.lowPrompt)} class="value-prompt"></div>
				<div ${ref(this.highPrompt)} class="value-prompt"></div>
			</div>
			<div ${ref(this.slider)} class="facet-range-slider"></div>
		`;
	}

	clearFacet () {
		this._internals.setFormValue('');
		this._internals?.form?.submit();
	}
};

window.customElements.define('x-facet-rangeslider', FacetRangeslider);
